import React, { useState } from "react";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { VariableSizeList } from "react-window";
import { RadioOption } from "../../../interface";
import { CustomCheckbox } from "../StyledCheckbox";

interface MultiSelectProps {
  id: string;
  name?: string;
  value: string[] | string;
  options: RadioOption[];
  label?: string;
  showLabel?: boolean;
  useMuiLabel?: boolean;
  placeholder?: string;
  onChange(event: SelectChangeEvent<string[] | string> | any): void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  testId?: string;
  className?: string;
  multiple?: boolean;
  isVirtualized?: boolean;
  isAutocomplete?: boolean;
}

const optionLabelFor = (options: RadioOption[], value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

const rowHeightFor = (options: RadioOption[], index: number) => {
  const length = options[index].label.length;
  if (length > 50) return 60;
  if (length > 30) return 50;
  return 40;
};

const renderValueFor =
  ({
    options,
    isMulti,
    useMuiLabel,
    placeholder,
  }: Pick<MultiSelectProps, "options" | "useMuiLabel" | "placeholder"> & {
    isMulti: boolean;
  }) =>
  (selected: unknown) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return useMuiLabel ? undefined : (
        <span style={{ color: "#aaa" }}>{placeholder}</span>
      );
    }
    if (isMulti && Array.isArray(selected)) {
      return selected.map((value) => optionLabelFor(options, value)).join(", ");
    }
    return optionLabelFor(options, selected as string);
  };

const OptionItem = ({
  option,
  isMulti,
  isSelected,
  onClick,
  style,
  virtualized,
}: {
  option: RadioOption;
  isMulti: boolean;
  isSelected(optionValue: string): boolean;
  onClick(optionValue: string): void;
  style?: React.CSSProperties;
  virtualized?: boolean;
}) => (
  <MenuItem
    value={option.value}
    style={style}
    onClick={() => onClick(option.value)}
  >
    {isMulti && (
      <CustomCheckbox size="small" checked={isSelected(option.value)} />
    )}
    <ListItemText
      primary={option.label}
      sx={virtualized ? { textWrap: "auto" } : undefined}
    />
  </MenuItem>
);

const OptionList = ({
  options,
  isMulti,
  isVirtualized,
  isSelected,
  onOptionClick,
}: {
  options: RadioOption[];
  isMulti: boolean;
  isVirtualized: boolean;
  isSelected(optionValue: string): boolean;
  onOptionClick(optionValue: string): void;
}) => {
  if (!isVirtualized) {
    return (
      <>
        {options.map((option) => (
          <OptionItem
            key={option.value}
            option={option}
            isMulti={isMulti}
            isSelected={isSelected}
            onClick={onOptionClick}
          />
        ))}
      </>
    );
  }
  return (
    <VariableSizeList
      height={Math.min(options.length, 8) * 40}
      itemCount={options.length}
      width="100%"
      itemData={options}
      itemSize={(index) => rowHeightFor(options, index)}
    >
      {({ index, style, data }) => (
        <OptionItem
          option={data[index]}
          isMulti={isMulti}
          isSelected={isSelected}
          onClick={onOptionClick}
          style={style}
          virtualized
        />
      )}
    </VariableSizeList>
  );
};

const AutocompleteSelect = ({
  name,
  value,
  options,
  label,
  showLabel,
  placeholder,
  onChange,
  error,
  helperText,
  disabled,
  testId,
  className,
  multiple,
}: Omit<
  MultiSelectProps,
  "id" | "useMuiLabel" | "isVirtualized" | "isAutocomplete"
>) => (
  <FormControl
    fullWidth
    error={error}
    disabled={disabled}
    className={className}
  >
    <Autocomplete
      data-testid={testId}
      multiple={multiple}
      disableCloseOnSelect
      limitTags={4}
      options={options.map((option) => option.value)}
      value={value as string[]}
      onChange={(_event, newValue) =>
        onChange({ target: { name, value: newValue } })
      }
      getOptionLabel={(option) => optionLabelFor(options, option)}
      isOptionEqualToValue={(option, selectedValue) => option === selectedValue}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <CustomCheckbox checked={selected} size="small" />
          {optionLabelFor(options, option)}
        </li>
      )}
      noOptionsText="No options"
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={error}
          label={showLabel ? label : undefined}
          helperText={helperText}
          InputLabelProps={{
            shrink: Boolean(Array.isArray(value) ? value.length : value),
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  maxHeight: "75px",
                  overflowY: "auto",
                }}
              >
                {params.InputProps.startAdornment}
              </div>
            ),
          }}
        />
      )}
      ListboxProps={{ style: { maxHeight: 250 } }}
      sx={{
        "&.MuiAutocomplete-hasPopupIcon": {
          ".MuiAutocomplete-inputRoot": { paddingRight: "9px !important" },
        },
        ".MuiAutocomplete-endAdornment": { top: "calc(100% - 40px)" },
      }}
    />
  </FormControl>
);

const SelectLabel = ({
  id,
  label,
  showLabel,
  useMuiLabel,
}: Pick<MultiSelectProps, "id" | "label" | "showLabel" | "useMuiLabel">) => {
  if (!showLabel || !label) return null;
  if (useMuiLabel)
    return (
      <InputLabel id={`${id}-label`} htmlFor={id}>
        {label}
      </InputLabel>
    );
  return (
    <FormLabel component="span" id={`${id}-label`}>
      {label}
    </FormLabel>
  );
};

// eslint-disable-next-line complexity
const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  name,
  value,
  options,
  label,
  showLabel = true,
  useMuiLabel = false,
  placeholder = "Select options",
  onChange,
  error = false,
  helperText,
  disabled = false,
  testId,
  className,
  multiple = true,
  isVirtualized = false,
  isAutocomplete = false,
}) => {
  // Options are wrapped in OptionList, so MUI Select cannot auto-close on item click.
  const [open, setOpen] = useState(false);
  const isSelected = (optionValue: string) =>
    multiple && Array.isArray(value)
      ? value.includes(optionValue)
      : value === optionValue;
  const handleOptionClick = (optionValue: string) => {
    if (!multiple) {
      onChange({
        target: { name, value: optionValue },
      } as SelectChangeEvent<string>);
      setOpen(false);
      return;
    }
    const selectedValues = value as string[];
    const nextValue = isSelected(optionValue)
      ? selectedValues.filter((value) => value !== optionValue)
      : [...selectedValues, optionValue];
    onChange({ target: { name, value: nextValue } } as SelectChangeEvent<
      string[]
    >);
  };

  if (isAutocomplete) {
    return (
      <AutocompleteSelect
        name={name}
        value={value}
        options={options}
        label={label}
        showLabel={showLabel}
        placeholder={placeholder}
        onChange={onChange}
        error={error}
        helperText={helperText}
        disabled={disabled}
        testId={testId}
        className={className}
        multiple={multiple}
      />
    );
  }

  return (
    <FormControl
      fullWidth
      error={error}
      disabled={disabled}
      className={className}
    >
      <SelectLabel
        id={id}
        label={label}
        showLabel={showLabel}
        useMuiLabel={useMuiLabel}
      />
      <Select
        id={id}
        data-testid={testId}
        name={name}
        MenuProps={{ disablePortal: true }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        multiple={multiple}
        displayEmpty={!useMuiLabel}
        labelId={`${id}-label`}
        label={useMuiLabel ? label : undefined}
        renderValue={renderValueFor({
          options,
          isMulti: multiple,
          useMuiLabel,
          placeholder,
        })}
        error={error}
        disabled={disabled}
        input={useMuiLabel ? <OutlinedInput label={label} /> : undefined}
        sx={{
          borderRadius: "2px",
          "&.Mui-focused": { borderColor: "#0B41CD" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "2px" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0B41CD",
          },
        }}
      >
        {useMuiLabel && !multiple && (
          <MenuItem value="" disabled>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        <OptionList
          options={options}
          isMulti={multiple}
          isVirtualized={isVirtualized}
          isSelected={isSelected}
          onOptionClick={handleOptionClick}
        />
      </Select>
      {helperText && (
        <FormHelperText sx={{ ml: 0 }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default MultiSelect;
